import Web3 from 'web3'
import { DAPP_CONFIG } from '../config/dapp'

class WalletState {
  wallet = {
    chainId: null,
    account: null,
    lang: 'Ch',
    hasProvider: false,
  }

  config = {
    Pool: DAPP_CONFIG.wallet.poolAddress,
  }

  listeners = []
  subscribedProvider = null

  init() {
    this.subscribeWeb3()
    this.getConfig()
    this.syncWallet()
  }

  getProvider() {
    const okxProvider = window.okxwallet && window.okxwallet.ethereum
    if (okxProvider) {
      return okxProvider
    }

    const ethereum = window.ethereum
    if (ethereum && Array.isArray(ethereum.providers) && ethereum.providers.length) {
      const providers = ethereum.providers.filter(Boolean)
      return (
        providers.find((provider) => provider && (provider.isOkxWallet || provider.isOKExWallet || provider.isOKXWallet)) ||
        providers.find((provider) => provider && provider.isMetaMask) ||
        providers.find((provider) => provider && !provider.isTronLink) ||
        providers[0]
      )
    }

    return ethereum || Web3.givenProvider || null
  }

  subscribeWeb3() {
    const provider = this.getProvider()
    if (!provider || !provider.on || this.subscribedProvider === provider) {
      return
    }

    this.subscribedProvider = provider
    provider.on('accountsChanged', () => {
      this.syncWallet()
    })
    provider.on('chainChanged', () => {
      this.syncWallet()
    })
  }

  async getConfig() {
    const storage = window.localStorage
    if (storage && storage.language) {
      this.wallet.lang = storage.language
    }
    this.notifyAll()
  }

  async resolveChainId(provider, web3) {
    try {
      if (provider && provider.request) {
        const chainIdValue = await provider.request({ method: 'eth_chainId' })
        if (chainIdValue !== undefined && chainIdValue !== null && chainIdValue !== '') {
          if (typeof chainIdValue === 'number') {
            return chainIdValue
          }
          const normalized = String(chainIdValue).trim().toLowerCase()
          if (normalized.startsWith('0x')) {
            return parseInt(normalized, 16)
          }
          return parseInt(normalized, 10)
        }
      }
    } catch (error) {}

    try {
      return await web3.eth.getChainId()
    } catch (error) {
      return null
    }
  }

  isExpectedChainId(chainId) {
    return Number(chainId) === Number(DAPP_CONFIG.wallet.chainId)
  }

  async syncWallet() {
    this.subscribeWeb3()
    const provider = this.getProvider()
    this.wallet.hasProvider = !!provider
    if (!provider) {
      this.wallet.chainId = null
      this.wallet.account = null
      this.notifyAll()
      return
    }

    Web3.givenProvider = provider
    const web3 = new Web3(provider)
    this.wallet.chainId = await this.resolveChainId(provider, web3)

    try {
      const accounts = await provider.request({ method: 'eth_accounts' })
      this.wallet.account = accounts && accounts.length ? accounts[0] : null
    } catch (error) {
      this.wallet.account = null
    }

    this.notifyAll()
  }

  async ensureWallet(requestAccounts = false) {
    const provider = this.getProvider()
    this.wallet.hasProvider = !!provider
    if (!provider) {
      this.wallet.chainId = null
      this.wallet.account = null
      this.notifyAll()
      throw new Error('\u672a\u68c0\u6d4b\u5230\u94b1\u5305\uff0c\u8bf7\u5148\u5b89\u88c5 OKX Wallet \u6216 MetaMask\u3002')
    }

    Web3.givenProvider = provider
    const web3 = new Web3(provider)

    if (requestAccounts) {
      const accounts = await provider.request({ method: 'eth_requestAccounts' })
      this.wallet.account = accounts && accounts.length ? accounts[0] : null
    } else {
      try {
        const accounts = await provider.request({ method: 'eth_accounts' })
        this.wallet.account = accounts && accounts.length ? accounts[0] : null
      } catch (error) {
        this.wallet.account = null
      }
    }

    this.wallet.chainId = await this.resolveChainId(provider, web3)

    if (!this.isExpectedChainId(this.wallet.chainId) && provider.request) {
      try {
        await provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: '0x38' }],
        })
        this.wallet.chainId = await this.resolveChainId(provider, web3)
      } catch (error) {}
    }

    this.notifyAll()
    return { ...this.wallet }
  }

  async connectWallet() {
    const wallet = await this.ensureWallet(true)
    return wallet.account
  }

  async connetWallet() {
    return this.connectWallet()
  }

  onStateChanged(cb) {
    this.listeners.push(cb)
    cb({ ...this.wallet })
  }

  removeListener(cb) {
    this.listeners = this.listeners.filter((item) => item !== cb)
  }

  notifyAll() {
    const payload = { ...this.wallet }
    this.listeners.forEach((cb) => cb(payload))
  }
}

export const CHAIN_ID = DAPP_CONFIG.wallet.chainId
export const CHAIN_SYMBOL = DAPP_CONFIG.wallet.chainSymbol
export const CHAIN_ERROR_TIP = DAPP_CONFIG.wallet.chainErrorTip
export const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'
export const MAX_INT = '115792089237316195423570985008687907853269984665640564039457584007913129639935'

export default new WalletState()
